import {ScheduledPaymentType} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {Plan} from "./TransferWizardSteps";
import {roundToCents} from "../../../../shared/money";

/**
 * Zeilen der Karten „Bisherige"/„Neue Buchung" (Zusammenfassung und Ergebnis-Dialog) aus dem Plan.
 * ADR 0012: übernommene Posten, die in der alten Buchung bezahlt waren, sind auf B per Übertrag
 * bezahlt — sie stehen in der Kostenaufstellung mit Haken und fehlen im Zahlungsplan.
 */
export type PlanRate = Plan["rates"][number];
type PlanLike = Pick<Plan, "rates" | "itemReview" | "paidA">;
type Money = (amount: number) => string;

const byDue = (rates: Array<PlanRate>): Array<PlanRate> => [...rates].sort((a, b) => String(a.due).localeCompare(String(b.due)));

/** Übernommene Posten und Lektions-Posten als Zeilen der Kostenaufstellung, chronologisch. */
export const carriedRates = (plan: PlanLike): Array<PlanRate> => byDue(plan.rates.filter((rate) => rate.origin !== "plan"));

/** Zahlungsplan: Kursraten immer, Posten nur solange sie nicht voll gedeckt sind. */
export const scheduleRates = (plan: PlanLike): Array<PlanRate> => byDue(plan.rates).filter((rate) => rate.origin === "plan" || !rate.fullyCovered);

/** Übernommene Posten, die in der alten Buchung (teil-)bezahlt waren. Gebühren bleiben dort und stecken nicht in paidA. */
const paidInSourceItems = (plan: PlanLike) => plan.itemReview.filter((item) => (item.type === ScheduledPaymentType.AdditionalItem || item.type === ScheduledPaymentType.Custom) && item.carried > 0 && item.paid > 0);

/** Unterzeile zu „Angerechnet aus …": was der Übertrag deckt — in A bezahlte Posten, weitere Posten, Anteil auf den Kursraten. */
export const appliedNote = (plan: PlanLike, money: Money): string | undefined => {
    const tr = i18n.containers.bookings.TransferWizard.summary;
    const covered = carriedRates(plan).filter((rate) => rate.fullyCovered).length;

    if (covered === 0) return undefined;
    // „dort bereits bezahlt" nur für Posten, die in A voll bezahlt waren (itemReview): `fullyCovered`
    // beschreibt allein die Rate von B — auch ein in A offener Posten oder der Lektions-Posten kann
    // durch übrig bleibenden Übertrag gedeckt sein (Review #998). ADR 0012 garantiert, dass in A voll
    // bezahlte Posten auf B immer gedeckt sind, deshalb ist die Differenz nie negativ.
    // Vergleich gegen `carried` wie der Planner gegen die Rate von B: bei einem abgewählten, teilbezahlten
    // Posten wandert nur der bezahlte Anteil mit (carried = paid) — der gilt dort als bezahlt.
    const paidInSource = Math.min(covered, paidInSourceItems(plan).filter((item) => item.paid >= item.carried).length);
    const planRates = plan.rates.filter((rate) => rate.origin === "plan");
    const onCourse = roundToCents(planRates.reduce((sum, rate) => sum + rate.settledByTransfer, 0));
    const parts: Array<string> = [];

    if (paidInSource > 0) parts.push(tr.itemsPaidInSource({count: String(paidInSource)}));
    if (covered > paidInSource) parts.push(tr.itemsCoveredByTransfer({count: String(covered - paidInSource)}));
    if (onCourse > 0) parts.push(planRates.length > 1 ? tr.onCourseRates({amount: money(onCourse)}) : tr.onCourseRate({amount: money(onCourse)}));

    return parts.join(" · ");
};

/** Unterzeile zu „Bezahlt" der alten Buchung: wofür das Geld war (Posten, Rest Kursrate). */
export const paidInSourceNote = (plan: PlanLike, money: Money): string | undefined => {
    const tr = i18n.containers.bookings.TransferWizard.summary;
    const paidItems = paidInSourceItems(plan);

    if (paidItems.length === 0) return undefined;
    const onCourse = roundToCents(plan.paidA - paidItems.reduce((sum, item) => sum + item.paid, 0));

    return `${paidItems.map((item) => item.title).join(", ")} · ${onCourse > 0 ? tr.courseRatePaid({amount: money(onCourse)}) : tr.courseRateOpen()}`;
};
